import React, { useMemo, useState } from 'react';
import { Box, Typography, Paper, Container, Tooltip, IconButton, Button } from '@mui/material';
import { TrendingUp, People, VideoLibrary, MonetizationOn, AccessTime, ExpandMore, ExpandLess, Android, Apple } from '@mui/icons-material';
import DreamyAppsLogo from './DreamyAppsLogo';

const WelcomeHeader = ({ user, isAdmin = false, middleContent = null }) => {
  const [showPreviousMonth, setShowPreviousMonth] = useState(false);
  
  const { previousStats, currentStats } = useMemo(() => {
    const getNumber = (obj, keys, fallback = 0) => {
      if (!obj) return fallback;
      for (const key of keys) {
        if (obj[key] !== undefined && obj[key] !== null) {
          const parsed = Number(obj[key]);
          if (!Number.isNaN(parsed)) {
            return parsed;
          }
        }
      }
      return fallback;
    };

    const getNullableNumber = (obj, keys) => {
      if (!obj) return null;
      for (const key of keys) {
        if (obj[key] !== undefined && obj[key] !== null) {
          const parsed = Number(obj[key]);
          if (!Number.isNaN(parsed)) {
            return parsed;
          }
        }
      }
      return null;
    };

    const formatCurrency = (value) =>
      new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);

    const formatNumber = (value) =>
      new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 0,
      }).format(value);

    const formatMinutes = (value) => {
      const minutes = Number(value);
      if (!Number.isFinite(minutes) || minutes <= 0) return '0 min';
      if (minutes < 60) return `${Math.round(minutes)} min`;
      const hours = Math.floor(minutes / 60);
      const remaining = Math.round(minutes % 60);
      if (remaining === 0) return `${hours}h`;
      return `${hours}h ${remaining}m`;
    };

    const formatNullableCurrency = (value) => {
      if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return '—';
      }
      return formatCurrency(Number(value));
    };

    const balance = getNumber(user, ['balance', 'Balance', 'totalEngagementShare']);
    const referralCount = getNumber(user, ['referralCount', 'Referrals', 'ActiveConnections']);
    const engagementMinutes = getNumber(user, ['engagementMinutes', 'EngagementMinutes', 'videosWatched', 'VideosWatched']);
    const transfers = getNumber(user, [
      'lastTransferAmount',
      'LastTransferAmount',
      'transfers',
      'Transfers',
      'TotalTransfers',
      'withdrawalsUSD',
    ]);
    const currentMonthTokens = getNumber(user, ['currentMonthTokens', 'CurrentMonthTokens']);
    const currentMonthEstimatedUSD = getNullableNumber(user, ['currentMonthEstimatedUSD', 'CurrentMonthEstimatedUSD']);
    const currentMonthMinutes = getNumber(user, ['currentMonthMinutes', 'CurrentMonthMinutes']);
    const currentMonthConnections = getNumber(user, ['currentMonthNewConnections', 'CurrentMonthNewConnections']);

    const previous = [
      {
        icon: <TrendingUp />,
        label: 'Total Engagement Share',
        value: formatCurrency(balance),
        color: '#4caf50',
        tooltip: 'Represents your overall participation in community ad engagement.',
      },
      {
        icon: <People />,
        label: 'Community Connections',
        value: formatNumber(referralCount),
        color: '#2196f3',
        tooltip: 'Number of users connected to you within your community network.',
      },
      {
        icon: <VideoLibrary />,
        label: 'Total Engagement Time',
        value: formatMinutes(engagementMinutes),
        color: '#ff9800',
        tooltip: 'Estimated viewing time from your most recent engagement period.',
      },
      {
        icon: <MonetizationOn />,
        label: 'Last Transfer',
        value: formatCurrency(transfers),
        color: '#9c27b0',
        tooltip: 'Most recent approved transfer processed from your balance.',
      },
    ];

    const current = [
      {
        icon: <TrendingUp />,
        label: 'Engagement Points (MTD)',
        value: formatNumber(currentMonthTokens),
        color: '#4caf50',
        tooltip: 'Tokens generated from your own views so far this month.',
      },
      {
        icon: <People />,
        label: 'New Connections (MTD)',
        value: formatNumber(currentMonthConnections),
        color: '#2196f3',
        tooltip: 'Direct referrals who joined under you this month.',
      },
      {
        icon: <AccessTime />,
        label: 'Activity Time (MTD)',
        value: formatMinutes(currentMonthMinutes),
        color: '#9c27b0',
        tooltip: 'Estimated watch time earned so far this month.',
      },
    ];

    return { previousStats: previous, currentStats: current };
  }, [user]);

  return (
    <Box sx={{ mb: { xs: 1, sm: 2 } }}>
      {/* Hero Section */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 2,
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 1.5, sm: 2 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: { xs: '100px', sm: '150px' },
            height: { xs: '100px', sm: '150px' },
            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            borderRadius: '50%',
            transform: 'translate(50%, -50%)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: { xs: '70px', sm: '100px' },
            height: { xs: '70px', sm: '100px' },
            background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
            borderRadius: '50%',
            transform: 'translate(-50%, 50%)',
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1, flexDirection: { xs: 'column', md: 'row' }, textAlign: { xs: 'center', md: 'left' } }}>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: { xs: 0.25, sm: 0.5 }, fontSize: { xs: '1rem', sm: '1.5rem', md: '1.75rem' } }}>
                Welcome To Dreamey Vids
              </Typography>
              <Typography variant="subtitle1" sx={{ opacity: 0.9, mb: { xs: 0.5, sm: 1 }, fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                {isAdmin ? 'Admin Dashboard' : 'Your Creative Journey Starts Here'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, maxWidth: '600px', fontSize: { xs: '0.65rem', sm: '0.875rem' } }}>
                {isAdmin 
                  ? 'Manage users, content, and platform operations with powerful admin tools.'
                  : 'Create amazing content, earn rewards, and build your community. Turn your creativity into income with our innovative platform.'
                }
              </Typography>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <DreamyAppsLogo size="large" showText={false} variant="colorful" />
            </Box>
          </Box>
        </Container>
      </Paper>

      {/* App Download Links */}
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 }, mb: 3 }}>
        <Paper
          elevation={0}
          sx={(theme) => ({
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            background:
              theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.06)'
                : 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(10px)',
            border:
              theme.palette.mode === 'dark'
                ? '1px solid rgba(255,255,255,0.08)'
                : '1px solid rgba(255,255,255,0.2)',
          })}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, mb: 2, color: (theme) => theme.palette.text.primary, fontSize: { xs: '0.875rem', sm: '1.125rem' } }}
          >
            Download our App
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: 'row', justifyContent: 'center' }}>
            <Button
              component="a"
              href="https://play.google.com/store/apps/details?id=com.dreamey.app&pcampaignid=web_share"
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              startIcon={<Android />}
              sx={{
                flex: { xs: '0 1 auto', sm: 'none' },
                backgroundColor: '#3DDC84',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#34C873',
                },
              }}
            >
              Android
            </Button>
            <Button
              component="a"
              href="https://apps.apple.com/pk/app/dreamey-vids/id6755639217"
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              startIcon={<Apple />}
              sx={{
                flex: { xs: '0 1 auto', sm: 'none' },
                backgroundColor: '#000000',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#333333',
                },
              }}
            >
              iOS
            </Button>
          </Box>
        </Paper>
      </Container>

      {/* Middle Content (e.g., alerts, notices) */}
      {middleContent && (
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 }, mb: 2 }}>
          {middleContent}
        </Container>
      )}

      {/* Stats Cards */}
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: (theme) => theme.palette.text.primary, fontSize: { xs: '0.9rem', sm: '1.5rem' } }}
          >
            Previous Month Engagement
          </Typography>
          <IconButton
            onClick={() => setShowPreviousMonth(!showPreviousMonth)}
            sx={{ color: (theme) => theme.palette.text.secondary }}
            aria-label={showPreviousMonth ? 'Hide previous month engagement' : 'Show previous month engagement'}
          >
            {showPreviousMonth ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
        {showPreviousMonth && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: { xs: 1, sm: 2 }, mb: 2, width: '100%', overflow: 'visible' }}>
            {previousStats.map((stat, index) => (
              <Tooltip key={index} title={stat.tooltip} placement="top" enterTouchDelay={0}>
                <Paper
                  elevation={0}
                  sx={(theme) => ({
                    p: { xs: 1.5, sm: 3 },
                    borderRadius: 2,
                    background:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.06)'
                        : 'rgba(255,255,255,0.8)',
                    backdropFilter: 'blur(10px)',
                    border:
                      theme.palette.mode === 'dark'
                        ? '1px solid rgba(255,255,255,0.08)'
                        : '1px solid rgba(255,255,255,0.2)',
                    transition: 'all 0.3s ease',
                    width: '100%',
                    minWidth: 0,
                    overflow: 'visible',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow:
                        theme.palette.mode === 'dark'
                          ? '0 8px 25px rgba(0,0,0,0.4)'
                          : '0 8px 25px rgba(0,0,0,0.15)',
                    },
                    cursor: 'default',
                  })}
                >
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, alignItems: { xs: 'center', sm: 'flex-start' }, justifyContent: { xs: 'space-between', sm: 'flex-start' }, width: '100%', height: '100%' }}>
                    {/* Icon and Label Container */}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, alignItems: { xs: 'center', sm: 'flex-start' }, mb: { xs: 0, sm: 2 }, flex: { xs: 1, sm: 'none' }, minWidth: 0, width: { xs: 'auto', sm: '100%' } }}>
                      <Box
                        sx={(theme) => ({
                          p: { xs: 0.75, sm: 1.5 },
                          borderRadius: 2,
                          background:
                            theme.palette.mode === 'dark'
                              ? `${stat.color}30`
                              : `${stat.color}15`,
                          color: stat.color,
                          mr: { xs: 1, sm: 0 },
                          mb: { xs: 0, sm: 1.5 },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          '& svg': {
                            fontSize: { xs: '18px', sm: '24px' }
                          }
                        })}
                      >
                        {stat.icon}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={(theme) => ({
                          fontWeight: 600,
                          letterSpacing: 0.2,
                          fontSize: { xs: '0.7rem', sm: '0.875rem' },
                          lineHeight: 1.2,
                          color:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255,255,255,0.9)'
                              : theme.palette.text.secondary,
                          whiteSpace: { xs: 'nowrap', sm: 'normal' },
                          overflow: { xs: 'hidden', sm: 'visible' },
                          textOverflow: { xs: 'ellipsis', sm: 'clip' },
                          flex: { xs: 1, sm: 'none' },
                          minWidth: 0,
                          width: { xs: 'auto', sm: '100%' },
                          textAlign: { xs: 'left', sm: 'left' },
                        })}
                      >
                        {stat.label}
                      </Typography>
                    </Box>
                    {/* Value */}
                    <Typography variant="h4" sx={{ fontWeight: 700, color: stat.color, fontSize: { xs: '1.25rem', sm: '2.125rem' }, flexShrink: 0, ml: { xs: 1, sm: 0 }, mt: { xs: 0, sm: 'auto' } }}>
                      {stat.value}
                    </Typography>
                  </Box>
                </Paper>
              </Tooltip>
            ))}
          </Box>
        )}

        <Typography
          variant="h5"
          sx={{ fontWeight: 700, mt: { xs: 1, sm: 4 }, mb: 2, color: (theme) => theme.palette.text.primary, fontSize: { xs: '0.9rem', sm: '1.0rem' } }}
        >
          Current Month Engagement
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: { xs: 1, sm: 2 }, width: '100%', overflow: 'visible' }}>
          {currentStats.map((stat, index) => (
            <Tooltip key={`current-${index}`} title={stat.tooltip} placement="top" enterTouchDelay={0}>
              <Paper
                elevation={0}
                sx={(theme) => ({
                  p: { xs: 1.5, sm: 3 },
                  borderRadius: 2,
                  background:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.06)'
                      : 'rgba(255,255,255,0.8)',
                  backdropFilter: 'blur(10px)',
                  border:
                    theme.palette.mode === 'dark'
                      ? '1px solid rgba(255,255,255,0.08)'
                      : '1px solid rgba(255,255,255,0.2)',
                  transition: 'all 0.3s ease',
                  width: '100%',
                  minWidth: 0,
                  overflow: 'visible',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow:
                      theme.palette.mode === 'dark'
                        ? '0 8px 25px rgba(0,0,0,0.4)'
                        : '0 8px 25px rgba(0,0,0,0.15)',
                  },
                  cursor: 'default',
                })}
              >
                <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, alignItems: { xs: 'center', sm: 'flex-start' }, justifyContent: { xs: 'space-between', sm: 'flex-start' }, width: '100%', height: '100%' }}>
                  {/* Icon and Label Container */}
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, alignItems: { xs: 'center', sm: 'flex-start' }, mb: { xs: 0, sm: 2 }, flex: { xs: 1, sm: 'none' }, minWidth: 0, width: { xs: 'auto', sm: '100%' } }}>
                    <Box
                      sx={(theme) => ({
                        p: { xs: 0.75, sm: 1.5 },
                        borderRadius: 2,
                        background:
                          theme.palette.mode === 'dark'
                            ? `${stat.color}30`
                            : `${stat.color}15`,
                        color: stat.color,
                        mr: { xs: 1, sm: 0 },
                        mb: { xs: 0, sm: 1.5 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        '& svg': {
                          fontSize: { xs: '18px', sm: '24px' }
                        }
                      })}
                    >
                      {stat.icon}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={(theme) => ({
                        fontWeight: 600,
                        letterSpacing: 0.2,
                        fontSize: { xs: '0.7rem', sm: '0.875rem' },
                        lineHeight: 1.2,
                        color:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.9)'
                            : theme.palette.text.secondary,
                        whiteSpace: { xs: 'nowrap', sm: 'normal' },
                        overflow: { xs: 'hidden', sm: 'visible' },
                        textOverflow: { xs: 'ellipsis', sm: 'clip' },
                        flex: { xs: 1, sm: 'none' },
                        minWidth: 0,
                        width: { xs: 'auto', sm: '100%' },
                        textAlign: { xs: 'left', sm: 'left' },
                      })}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                  {/* Value */}
                  <Typography variant="h4" sx={{ fontWeight: 700, color: stat.color, fontSize: { xs: '1.25rem', sm: '2.125rem' }, flexShrink: 0, ml: { xs: 1, sm: 0 }, mt: { xs: 0, sm: 'auto' } }}>
                    {stat.value}
                  </Typography>
                </Box>
              </Paper>
            </Tooltip>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default WelcomeHeader;
